export type ExceptionSimulateMode = 'off' | 'shadow' | 'on'

const VALID_EXCEPTION_SIMULATE_MODES: ExceptionSimulateMode[] = ['off', 'shadow', 'on']

function resolveExceptionSimulateMode(): ExceptionSimulateMode {
  const raw = process.env.VUE_APP_EXCEPTION_SIMULATE_MODE
  return VALID_EXCEPTION_SIMULATE_MODES.includes(raw as ExceptionSimulateMode)
    ? (raw as ExceptionSimulateMode)
    : 'off'
}

export function useFeatureFlags() {
  return {
    flags: {
      enabledPermissions: process.env.VUE_APP_ENABLED_PERMISSIONS === 'true',
      enabledPolicies: process.env.VUE_APP_ENABLED_POLICIES === 'true',
      enabledDSPoliciesPermissions: process.env.VUE_APP_ENABLED_DS_POLICIES_PERMISSIONS === 'true',
      enabledHandoverHistory: process.env.VUE_APP_ENABLED_HANDOVER_HISTORY === 'true',
      enabledManagementExceptionalities:
        process.env.VUE_APP_ENABLED_MANAGEMENT_EXCEPTIONALITIES === 'true',
      enabledObservationsSystem: process.env.VUE_APP_ENABLED_OBSERVATIONS_SYSTEM === 'true',
      enabledReports: process.env.VUE_APP_ENABLED_REPORTS === 'true',
      enabledStationHistory: process.env.VUE_APP_ENABLED_STATION_HISTORY === 'true',
      enabledManagement: process.env.VUE_APP_ENABLED_MANAGEMENT === 'true',
      enabledSmartAllocation: process.env.VUE_APP_ENABLED_SMART_ALLOCATION === 'true',
      enabledDoubleIdHeader: process.env.VUE_APP_ENABLED_DOUBLE_ID_HEADER === 'true',
      enabledDsAttendancePolicy: process.env.VUE_APP_ENABLED_DS_ATTENDANCE_POLICY === 'true',
      // PRD 32 — folha de monitoramento v2 (pré-preenchimento da data, classificação de
      // linha pela RN-03, bloqueio do avanço com motivo). Uma flag para o PRD inteiro.
      enabledDsAttendanceSheetV2: process.env.VUE_APP_ENABLED_DS_ATTENDANCE_SHEET_V2 === 'true',
      enabledMedicalEvaluation: process.env.VUE_APP_ENABLED_MEDICAL_EVALUATION === 'true',
      enabledApprovalFlow: process.env.VUE_APP_ENABLED_APPROVAL_FLOW === 'true',
      enabledApprovalEngine: process.env.VUE_APP_ENABLED_APPROVAL_ENGINE === 'true',
      // Exige Análise de Risco além de Operação na raiz do fluxo de excepcionalidade,
      // com o operador travado em "Todos - E". Ausente = false = só Operação obrigatória.
      enabledMandatoryRiskValidator:
        process.env.VUE_APP_ENABLED_MANDATORY_RISK_VALIDATOR === 'true',
      enabledHandoverV2: process.env.VUE_APP_ENABLED_HANDOVER_V2 === 'true',
      enabledExceptionalitySummary: process.env.VUE_APP_ENABLED_EXCEPTIONALITY_SUMMARY === 'true',
      enabledWebPushNotifications: process.env.VUE_APP_ENABLED_WEB_PUSH_NOTIFICATIONS === 'true',
      enabledFacialBiometry: process.env.VUE_APP_ENABLED_FACIAL_BIOMETRY === 'true',
      // PRD 00 — fila de avaliação de evidências dos registros de presença lançados no painel.
      enabledCheckValidation: process.env.VUE_APP_ENABLED_CHECK_VALIDATION === 'true',
      // Home de /aprovacoes em cards (uma rota por fila) no lugar das abas.
      enabledApprovalsHub: process.env.VUE_APP_ENABLED_APPROVALS_HUB === 'true',
      enabledClarity: process.env.VUE_APP_ENABLED_CLARITY === 'true',
      enabledPolicyModules: process.env.VUE_APP_ENABLED_POLICY_MODULES === 'true',
      enabledPostStation: process.env.VUE_APP_ENABLED_POST_STATION === 'true',
      enabledConsecutiveHoursLimit: process.env.VUE_APP_ENABLED_CONSECUTIVE_HOURS_LIMIT === 'true',
      enabledPermanenceRecord: process.env.VUE_APP_ENABLED_PERMANENCE_RECORD === 'true',
      // Fila "Pagamentos prioritários" (À vista + Antecipe). Depende do módulo
      // `priority-payments` do backend estar em PROD.
      enabledPriorityPayments: process.env.VUE_APP_ENABLED_PRIORITY_PAYMENTS === 'true',
      // Dashboard mensal: Setor/Especialidade, lentes, Mês e Calendário.
      enabledDashboardV2: process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true',
      // Onda 2.1: Matriz Setor × Especialidade. Só vale com a Dashboard V2 também ligada.
      enabledDashboardMatrix:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MATRIX === 'true',
      // Onda 2.3: Timeline por setor (visão de um dia). Só vale com a Dashboard V2 também ligada.
      enabledDashboardTimeline:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_TIMELINE === 'true',
      // Central de vagas (Onda 2.5a): vagas abertas e aguardando confirmação. Independe da V2.
      enabledDashboardVacancyCenter:
        process.env.VUE_APP_ENABLED_DASHBOARD_VACANCY_CENTER === 'true',
      // Tela Demanda (Onda 2.2): só vale junto com a Dashboard V2.
      enabledDashboardDemand:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_DEMAND === 'true',
      // Mapa da unidade + Modo TV (Onda 2.4): só vale junto com a Dashboard V2.
      enabledDashboardMap:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MAP === 'true',
      // Layout de celular do /dashboard (spec 2026-09-30). Só vale com a Dashboard V2 ligada.
      enabledDashboardMobile:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE === 'true',
      // Aba Solicitações v2 (spec 2026-09-25, Onda 1) e o filtro "Modalidade de registro"
      // nas outras abas. Depende do PR5 do backend (`activity-requests/*`) em PROD.
      enabledActivityRequestsV2: process.env.VUE_APP_ENABLED_ACTIVITY_REQUESTS_V2 === 'true',
      // Portal do Afiliado Antecipe (spec 2026-09-30): layout `affiliate`, rotas /afiliado/*,
      // Gestão › Afiliados e "ver portal". Sem a flag as rotas não existem (NotFound).
      enabledAntecipeAffiliate: process.env.VUE_APP_ENABLED_ANTECIPE_AFFILIATE === 'true',
      enabledSplitScheduleVisibility:
        process.env.VUE_APP_ENABLED_SPLIT_SCHEDULE_VISIBILITY === 'true',
      // Bloqueio temporário do upload de evidência pelo painel (set/2026), LIGADO
      // em PROD desde 10/09.
      //
      // F2/Q32 manda substituí-lo pelo bloqueio de verdade, servido pelo backend
      // em `registration-settings` (`manualCheckBlockEnabled`). Mas os dois
      // deploys não são o mesmo: enquanto a rota do backend não existir, ela
      // responde 404 e o campo cai no default `false` — remover a env var aqui
      // reabriria em PROD exatamente o upload que o kill switch fechou.
      //
      // Por isso a flag CONTINUA: é o fallback enquanto o campo do backend não
      // chega. Com `manualCheckBlockEnabled` presente, quem manda é ele.
      // Removê-la é tarefa de follow-up, depois da flag do backend ligada em PROD.
      // Ausente = false = comportamento atual (upload liberado).
      disabledManualEvidenceUpload: process.env.VUE_APP_DISABLED_MANUAL_EVIDENCE_UPLOAD === 'true',
      exceptionSimulateMode: resolveExceptionSimulateMode()
    }
  }
}
